import {
  Button,
  createStyles,
  withStyles,
  type WithStyles,
} from "@material-ui/core";
import { useTranslations } from "adapters/i18n";
import React, { useRef, type CSSProperties, type ReactNode } from "react";
import Dropzone from "react-dropzone";

type BaseProps = {
  acceptedFileTypes?: string[];
  children?: ReactNode;
  className?: string;
  isDisabled?: boolean;
  multiple?: boolean;
  onFileSelected: File | File[] extends infer T
    ? T extends File[]
      ? (files: File[]) => void
      : (file: File) => void
    : never;
  styles?: CSSProperties;
} & WithStyles<typeof styles>;

type FileUploadButtonProps =
  | (BaseProps & {
      multiple: true;
      onFileSelected: (files: File[]) => void;
    })
  | (BaseProps & {
      multiple: false;
      onFileSelected: (file: File) => void;
    })
  | (BaseProps & { onFileSelected: (files: File) => void });

function FileUploadButton({
  acceptedFileTypes,
  children,
  className,
  classes,
  isDisabled = false,
  multiple = false,
  onFileSelected,
  ...rest
}: FileUploadButtonProps) {
  const fileTypes = acceptedFileTypes?.join(",");

  const { t } = useTranslations();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);

    // Reset the input so the same file can be selected again
    if (inputRef.current) {
      inputRef.current.value = "";
    }

    if (files.length === 0) {
      return;
    }

    if (multiple) {
      (onFileSelected as (files: File[]) => void)(files);
      return;
    }

    (onFileSelected as (file: File) => void)(files[0]);
  };

  const handleOpenFileDialog = () => {
    inputRef.current?.click();
  };

  return (
    <>
      <Dropzone
        className={classes.dropzone}
        disabled={isDisabled}
        disableClick={true}
        disablePreview={true}
      >
        <Button
          aria-disabled={isDisabled}
          className={className}
          color="primary"
          data-testid="file-upload-button"
          disabled={isDisabled}
          onClick={handleOpenFileDialog}
          variant="contained"
          {...rest}
        >
          {children}
          <input
            accept={fileTypes}
            aria-label={t("common:file-upload")}
            data-testid="file-upload-input"
            hidden={true}
            multiple={multiple}
            onChange={handleFileChange}
            ref={inputRef}
            type="file"
          />
        </Button>
      </Dropzone>
    </>
  );
}

const styles = () =>
  createStyles({
    dropzone: {
      border: "none",
      height: "fit-content",
      opacity: 1,
      width: "fit-content",
    },
  });

export default withStyles(styles)(FileUploadButton);
